import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
// @ts-ignore
import naijaXbyState from "naija-xbystate";
import { InputGenerator } from "./components/InputGenerator";
import { BuilderTabs } from "./FormGeneratorTypes";
import { getRequiredFields } from "../utils";

export type FormGeneratorProps = {
  tabs: any[];
  onSubmit: (data: any) => void;
  step: number;
  theme?: any;
  children?: React.ReactNode;
  cardComponent?: React.ReactNode;
  bankComponent?: React.ReactNode;
  supportingDocComponent?: React.ReactNode;
  defaultFormValues: any;
  kyc?: boolean;
};

const FormGenerator = ({
  tabs,
  onSubmit,
  step,
  theme,
  cardComponent,
  bankComponent,
  children,
  defaultFormValues,
  kyc,
}: FormGeneratorProps) => {
  const [linkedTabs, unlinkedTabs]: [BuilderTabs[], BuilderTabs[]] = (
    tabs || []
  ).reduce(
    (acc, item) => {
      if (item.linkedToOption) {
        acc[0].push(item);
      } else {
        acc[1].push(item);
      }
      return acc;
    },
    [[], []]
  );
  const [tabIndex, setTabIndex] = useState(step);
  const [selectedState, setSelectedState] = useState("");
  const [updatedTabs, setUpdatedTabs] = useState(unlinkedTabs);
  const [defaultValue, setDefaultValues] = useState(
    defaultFormValues ? defaultFormValues[updatedTabs[step]?.name] : null
  );

  const [state, setState] = useState<any>({
    [updatedTabs[tabIndex]?.name]: { files: [] },
  });
  const [lga, setLga] = useState<string[]>([]);
  const [senatorialDistricts, setSenatorialDistricts] = useState<string[]>([]);
  const [showButton, setShowButton] = useState(true);
  const [requiredField, setRequiredField] = useState(
    getRequiredFields(updatedTabs[tabIndex])
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
  } = useForm({
    values: defaultValue,
  });

  useEffect(() => {
    const update = getRequiredFields(updatedTabs[tabIndex]);
    setRequiredField(update);
  }, [updatedTabs]);

  useEffect(() => {
    if (defaultValue?.stateId) {
      setSelectedState(defaultValue.stateId);
      setSenatorialDistricts(
        naijaXbyState?.senatorialDistricts(defaultValue.stateId)
      );
    }
  }, [defaultValue]);

  useEffect(() => {
    if (defaultValue) {
      Object.entries(defaultValue).forEach(([key, value]) => {
        if (key.toLowerCase().includes("state")) {
          setSelectedState(value as string);
          setSenatorialDistricts(
            naijaXbyState?.senatorialDistricts(value as string)
          );
          setLga(naijaXbyState?.lgas(value as string));
        }
      });
    }
  }, [defaultValue]);

  const submit = (data: any) => {
    const dropdowns = updatedTabs
      .flatMap((tab) => tab.builders)
      .filter((builder) => builder.type === "dropdown");
    const options = dropdowns.flatMap((d) => d.options);
    const newData: any = {};
    for (const key in data) {
      const option = options.find((opt) => opt.id === data[key]);
      newData[key] = option ? option.option : data[key];
    }
    const name = updatedTabs[tabIndex]?.name;
    onSubmit({ [name]: { ...newData, files: state[name]?.files } });
  };

  useEffect(() => {
    if (selectedState) {
      setLga(naijaXbyState?.lgas(selectedState));
      setSenatorialDistricts(naijaXbyState?.senatorialDistricts(selectedState));
    }
  }, [selectedState]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tabName: string
  ) => {
    const { value, name } = e.target;
    const newState = { ...state };
    newState[tabName] = newState[tabName]
      ? { ...newState[tabName], [name]: value }
      : { [name]: value };

    setState(newState);
  };

  const handleDateChange = (date: Date, tabName: string, inputName: string) => {
    date ? date.toISOString() : "";
    const newState = { ...state };
    newState[tabName] = newState[tabName]
      ? { ...newState[tabName], [inputName]: date ? date.toISOString() : "" }
      : { [inputName]: date ? date.toISOString() : "" };

    setState(newState);
  };
  const handleSelectChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tabName: string,
    type: string
  ) => {
    const { value } = e.target;
    if (type === "state") {
      setSelectedState(value);
    }
    handleInputChange(e, tabName);
  };

  const handleFileChange = (tabName: string, fileObj: any) => {
    const newState = { ...state };
    const index = newState[tabName].files.findIndex(
      (file: any) => file.name === fileObj.name
    );
    if (index > -1) newState[tabName].files[index] = fileObj;
    else newState[tabName].files = [...newState[tabName].files, fileObj];
    setState(newState);
  };

  const handleLinkForm = (
    tabId: string,
    newBuilder: any,
    builderId: string
  ) => {
    setUpdatedTabs((prevTabs) => {
      return prevTabs.map((res) => {
        if (res.id === tabId) {
          const oldBuilder = res.builders.filter(
            (b: any) => b.linkedTo === builderId
          );
          if (oldBuilder.length) {
            const idsToRemove = oldBuilder.map((item: any) => item.id);
            res.builders = res.builders.filter(
              (item: any) => !idsToRemove.includes(item.id)
            );
          }
          const insertIndex =
            res.builders.findIndex((res: any) => res.id === builderId) + 1;
          if (newBuilder && newBuilder.length) {
            return {
              ...res,
              builders: [
                ...res.builders.slice(0, insertIndex),
                ...newBuilder.map((b: any) => ({ ...b, linkedTo: builderId })),
                ...res.builders.slice(insertIndex),
              ],
            };
          }
        }
        return res;
      });
    });
  };

  const styles = {
    wrapper: {
      width: "100%",
    },
    flex: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
    },
    h: {
      fontSize: "2.8rem",
    },
  };
  return (
    <div style={styles.wrapper}>
      <div style={styles.wrapper}>
        {updatedTabs[tabIndex]?.builders[0].title === "Card" ? (
          cardComponent
        ) : (
          <form
            style={{ ...styles.flex, flexDirection: "column" }}
            key={`${updatedTabs[tabIndex]?.id}${updatedTabs[tabIndex]?.name}`}
            onSubmit={handleSubmit(submit)}
          >
            {!kyc && <h4 style={styles.h}>{updatedTabs[tabIndex]?.name}</h4>}
            <div style={styles.wrapper}>
              {updatedTabs[tabIndex]?.builders.map((builder: BuilderTabs) => (
                <InputGenerator
                  key={`${builder.id}${updatedTabs[tabIndex]?.name}`}
                  builder={builder}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  handleLinkForm={handleLinkForm}
                  tabName={updatedTabs[tabIndex]?.name}
                  tabId={updatedTabs[tabIndex]?.id}
                  lgas={lga}
                  linkedTabs={linkedTabs}
                  senatorialDistricts={senatorialDistricts}
                  cardComponent={cardComponent}
                  bankComponent={bankComponent}
                  handleDateChange={handleDateChange}
                  theme={theme}
                  showButton={(show: boolean) => setShowButton(show)}
                  handleFileChange={handleFileChange}
                  errors={errors}
                  setSelectedState={setSelectedState}
                  register={register}
                  requiredField={requiredField}
                  control={control}
                  getValues={getValues}
                  setValue={setValue}
                  setLga={setLga}
                  setSenatorialDistricts={setSenatorialDistricts}
                />
              ))}
            </div>
            {showButton && <div style={styles.wrapper}>{children}</div>}
          </form>
        )}
      </div>
    </div>
  );
};

export default FormGenerator;
